import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { AuthService } from '../../services/auth.service';
import { ClientCrypto } from '../../utils/client-crypto';

@Component({
  selector: 'app-change-password-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './change-password-modal.component.html',
  styleUrl: './change-password-modal.component.scss'
})
export class ChangePasswordModalComponent {
  @Output() close = new EventEmitter<void>();

  oldPassword = '';
  newPassword = '';
  confirmPassword = '';
  errorMessage = '';
  successMessage = '';
  isLoading = false;

  constructor(
    private authService: AuthService,
    private clientCrypto: ClientCrypto
  ) {}

  async onSubmit(): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';

    // Validation
    if (!this.oldPassword || !this.newPassword || !this.confirmPassword) {
      this.errorMessage = 'changePassword.errors.allFieldsRequired';
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.errorMessage = 'changePassword.errors.passwordMismatch';
      return;
    }

    if (this.newPassword.length < 8) {
      this.errorMessage = 'changePassword.errors.passwordTooShort';
      return;
    }

    if (this.oldPassword === this.newPassword) {
      this.errorMessage = 'changePassword.errors.samePassword';
      return;
    }

    try {
      this.isLoading = true;

      // Encrypt passwords
      const encryptedOldPassword = await this.clientCrypto.encryptRSA(this.oldPassword);
      const encryptedNewPassword = await this.clientCrypto.encryptRSA(this.newPassword);

      // Call the API
      this.authService.changePassword(
        encryptedOldPassword,
        encryptedNewPassword
      ).subscribe({
        next: () => {
          this.isLoading = false;
          this.successMessage = 'changePassword.success';
          setTimeout(() => {
            this.onClose();
          }, 1500);
        },
        error: (error) => {
          this.isLoading = false;
          const message =
            (typeof error?.message === 'string' && error.message.length > 0)
              ? error.message
              : error?.error?.message;
          this.errorMessage = message || 'changePassword.errors.generic';
        }
      });
    } catch (error) {
      this.isLoading = false;
      this.errorMessage = 'changePassword.errors.encryption';
    }
  }

  onClose(): void {
    this.close.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }
}
